import { useEffect, memo, useState, useMemo } from 'react';
import { Steps, Row, Col, Skeleton } from 'antd';
import { difference, isArray } from 'lodash';
// component
import { Form, StyledModal, CustomNotification } from 'common/components';
// hooks
import {
  useGetQasRequiredFields,
  useCreateQasRequiredFields,
  useSaveClaimsVisibility,
} from 'hooks/qas-require-field/useQasRequireField';
import { useDispatchReloadPage } from 'hooks/useReloadPage';
import { useGetAllProductFields } from 'hooks/qas-require-field/useQasRequireField';
import { FormQasTargetMember, SectionQasRequireField } from './';

import {
  filterRequiredFieldsByGroupName,
  getAllFieldsByGroupName,
  getAllRequiredFields,
} from 'pages/home/ribbon/qas-require-field/components/utils';

import { checkIsDollarProduct } from 'utils/checkIsOwner';

import useSteps from 'pages/home/ribbon/qas-require-field/components/useStepsRequiredField';

import {
  ClaimsActions,
  RequiredFieldsActions,
  STEP_VALUES,
  TargetMemberActions,
} from 'pages/home/ribbon/qas-require-field/components/ModalFormQasRequireField';

import ClaimsVisibility from 'pages/home/ribbon/qas-require-field/components/ClaimsVisibility';

import './ModalFormQasRequireField.less';

const ModalCopyQasRequireFields = (props) => {
  const { id, isModalOpen, setIsModalOpen, formInstance } = props;
  const reloadPage = useDispatchReloadPage();
  const createQasRequiredFields = useCreateQasRequiredFields();
  const claimsVisibilityMutation = useSaveClaimsVisibility();

  const { data, isLoading } = useGetQasRequiredFields(id);
  const { data: productFieldGroups } = useGetAllProductFields();

  const [selectedItems, setSelectedItems] = useState();
  const [requiredProperties, setRequiredProperties] = useState([]);

  const [searchText, setSearchText] = useState('');
  const [formData, setFormData] = useState('');

  const productType = Form.useWatch('productType', formInstance);

  const [formVisibilityInstance] = Form.useForm();

  const visibleClaims = useMemo(() => data?.visibleClaims || [], [data]);

  const {
    isRenderClaimsVisibility,
    isExistClaims,
    isTargetMemberStep,
    setStep,
    step,
    onBackStep,
    onNextStep,
  } = useSteps({
    productFieldGroups,
    productType: formData?.productType,
  });

  const groupRequireField = () => {
    let newProductFieldGroups = [];
    let fieldsByProductType = [];
    let fieldsByMemberId = [];
    let newFieldsRequire = [...requiredProperties];

    if (productType === 'HBC / OTC' || productType === 'Non-Food') {
      newProductFieldGroups = productFieldGroups.filter(
        (val) => val?.groupName === 'Ingredients'
      );
    }
    fieldsByProductType = getAllRequiredFields(newProductFieldGroups);

    if (
      selectedItems?.length > 0 &&
      selectedItems[0]?.companyId === process.env.REACT_APP_DOLLAR_GENERAL_ID // The fields of Dollar General are required by default
    ) {
      const newFields = productFieldGroups.filter(
        (val) => val?.groupName === 'Product Contains'
      );
      fieldsByMemberId = getAllRequiredFields(newFields);
    }
    if (fieldsByProductType?.length > 0) {
      newFieldsRequire = difference(newFieldsRequire, fieldsByProductType);
    }
    if (fieldsByMemberId?.length > 0) {
      newFieldsRequire = difference(newFieldsRequire, fieldsByMemberId);
    }

    return newFieldsRequire;
  };

  const handleSetupRequiredFields = async () => {
    if (step === 0) {
      await formInstance.validateFields().then(async (value) => {
        setFormData(value);
        setStep(1);
      });
    } else {
      handleSubmit();
    }
  };

  const onCancel = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = () => {
    const targetMemberId =
      selectedItems?.length > 0 && selectedItems[0]?.companyId;

    const isDollarGeneral = checkIsDollarProduct(targetMemberId);

    const fieldsByGroupName = getAllFieldsByGroupName(productFieldGroups);
    const requiredFields = filterRequiredFieldsByGroupName(
      fieldsByGroupName,
      requiredProperties,
      formData?.productType,
      isDollarGeneral
    );

    let params = {
      targetMemberId: targetMemberId,
      productType: formData?.productType,
      requiredFields,
    };

    createQasRequiredFields.mutate(params, {
      onSuccess: async (response) => {
        if (response?.isSuccess) {
          CustomNotification.success('Copy successfully');
          isExistClaims && setStep(STEP_VALUES.CLAIMS_VISIBILITY);
        } else {
          CustomNotification.error(
            response?.message || 'Copy failed! Please try!'
          );
        }
      },
      onError: (error) => {
        CustomNotification.error(error?.message || 'Copy failed! Please try!');
      },
    });
  };

  useEffect(() => {
    if (!data) return;
    setRequiredProperties(data?.requiredFields);
  }, [JSON.stringify(data)]);

  useEffect(() => {
    if (requiredProperties?.length === 0 || !isArray(productFieldGroups)) {
      return;
    }
    const newFieldsRequire = groupRequireField();
    setRequiredProperties(newFieldsRequire);
  }, [productType, selectedItems]);

  const isEmptySelectedItems = !selectedItems || selectedItems.length === 0;
  const isEmptyRequiredProperties =
    !requiredProperties || requiredProperties.length === 0;

  return (
    <StyledModal
      visible={isModalOpen}
      title={'Copy QA Specification Required Fields'}
      maskClosable={false}
      onCancel={onCancel}
      width='60vw'
      bodyStyle={{
        height: '80vh',
      }}
      className='qa-spec-require-field__modal'
      footer={
        <>
          {step === STEP_VALUES.SELECT_TARGET_MEMBER && (
            <TargetMemberActions
              onClick={async () => {
                await formInstance.validateFields().then((value) => {
                  if (isTargetMemberStep) {
                    setFormData(value); // prevent setFormData to undefined
                  }
                  onNextStep();
                });
              }}
              isDisabledNextButton={isEmptySelectedItems}
            />
          )}
          {step === STEP_VALUES.SELECT_REQUIRED_PROPERTIES && (
            <RequiredFieldsActions
              isDisabledSaveButton={isEmptyRequiredProperties}
              isLoadingSaveRequiredFields={createQasRequiredFields.isLoading}
              isExistClaims={isExistClaims}
              onSkip={() => {
                onNextStep();
              }}
              onBack={onBackStep}
              onSetupRequiredFields={handleSetupRequiredFields}
            />
          )}

          {step === STEP_VALUES.CLAIMS_VISIBILITY && (
            <ClaimsActions
              onBack={onBackStep}
              isExistClaims={isExistClaims}
              onSaveClaims={() => {
                const targetMemberId =
                  selectedItems?.length > 0 && selectedItems[0]?.companyId;
                const claims = formVisibilityInstance.getFieldsValue();
                const params = {
                  productType: formData?.productType,
                  memberId: targetMemberId,
                  visibleClaims: isRenderClaimsVisibility
                    ? Object.entries(claims)
                        .filter(([, value]) => value)
                        .map(([key]) => key)
                    : [],
                };

                claimsVisibilityMutation.mutate(params, {
                  onSuccess: (response) => {
                    if (response?.isSuccess) {
                      CustomNotification.success(
                        'Claims visibility saved successfully'
                      );
                      onCancel();
                      reloadPage();
                    } else {
                      CustomNotification.error(
                        response?.message ||
                          'Failed to save claims visibility! Please try!'
                      );
                    }
                  },
                });
              }}
            />
          )}
        </>
      }
    >
      <Skeleton active loading={isLoading}>
        <Steps current={step}>
          <Steps.Step title='Select Target Member' />
          <Steps.Step title='Select Required Properties' />
          {isRenderClaimsVisibility && <Steps.Step title='Claims Visibility' />}
        </Steps>
        <Row style={{ marginTop: 16 }}>
          <Col span={24}>
            {step === STEP_VALUES.SELECT_TARGET_MEMBER ? (
              <FormQasTargetMember
                formInstance={formInstance}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                searchText={searchText}
                setSearchText={setSearchText}
                isCopy
              />
            ) : step === STEP_VALUES.SELECT_REQUIRED_PROPERTIES ? (
              <SectionQasRequireField
                formData={formData}
                targetMember={selectedItems}
                requiredProperties={requiredProperties}
                setRequiredProperties={setRequiredProperties}
              />
            ) : (
              <ClaimsVisibility
                formInstance={formVisibilityInstance}
                visibleClaims={visibleClaims}
                selectedItems={requiredProperties || []}
              />
            )}
          </Col>
        </Row>
      </Skeleton>
    </StyledModal>
  );
};

export default memo(ModalCopyQasRequireFields);